// Volume control widget — Tron Legacy styled speaker icon + vertical expandable slider
// Created dynamically, positioned top-right over the header.

import type { MusicPlayer } from './music-player';

// Geometric speaker icons (SVG inline)
const ICON_ON = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.5 8.5a5 5 0 010 7"/><path d="M19 5a10 10 0 010 14"/></svg>`;

const ICON_OFF = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>`;

export interface VolumeControl {
  dispose: () => void;
}

export function createVolumeControl(player: MusicPlayer): VolumeControl {
  let started = false;
  let lastVolume = player.getVolume() || 0.5;
  const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;

  // --- Build DOM (vertical layout: icon on top, slider panel below) ---
  const el = document.createElement('div');
  el.className = 'volume-control volume-control--muted';

  const btn = document.createElement('button');
  btn.className = 'volume-control__btn';
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Activer/couper le son');
  btn.innerHTML = ICON_OFF;

  const panel = document.createElement('div');
  panel.className = 'volume-control__panel';

  const slider = document.createElement('input');
  slider.className = 'volume-control__slider';
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.step = '1';
  slider.value = String(Math.round(player.getVolume() * 100));
  slider.setAttribute('aria-label', 'Volume');

  panel.appendChild(slider);
  el.appendChild(btn);
  el.appendChild(panel);
  document.body.appendChild(el);

  // --- Helpers ---
  function updateUI(): void {
    const muted = player.isMuted() || player.getVolume() === 0;
    btn.innerHTML = muted ? ICON_OFF : ICON_ON;
    el.classList.toggle('volume-control--muted', muted);
    document.body.classList.toggle('music-playing', !muted && started);
    slider.value = String(Math.round(player.getVolume() * 100));
  }

  // --- Interactions ---
  btn.addEventListener('click', () => {
    // First click: start playback
    if (!started) {
      player.setMuted(false);
      player.play();
      started = true;
      if (isTouchDevice) el.classList.add('volume-control--open');
      updateUI();
      return;
    }

    // On touch devices: also toggle panel visibility
    if (isTouchDevice) {
      el.classList.toggle('volume-control--open');
    }

    // Toggle mute
    if (player.isMuted()) {
      player.setMuted(false);
      if (player.getVolume() === 0) {
        player.setVolume(lastVolume);
      }
    } else {
      lastVolume = player.getVolume() || lastVolume;
      player.setMuted(true);
    }
    updateUI();
  });

  slider.addEventListener('input', () => {
    const v = parseInt(slider.value, 10) / 100;
    player.setVolume(v);
    if (v > 0 && player.isMuted()) {
      player.setMuted(false);
    }
    lastVolume = v > 0 ? v : lastVolume;

    if (!started) {
      player.play();
      started = true;
    }
    updateUI();
  });

  // Close panel on tap outside (touch devices)
  function onDocClick(e: Event): void {
    if (!el.contains(e.target as Node)) {
      el.classList.remove('volume-control--open');
    }
  }
  if (isTouchDevice) {
    document.addEventListener('click', onDocClick);
  }

  // --- Cleanup ---
  function dispose(): void {
    if (isTouchDevice) {
      document.removeEventListener('click', onDocClick);
    }
    el.remove();
  }

  return { dispose };
}
